import type { CSSProperties, ReactElement, ReactNode } from "react";
import { color, radius, space } from "@tokens/index";
import { Button, Text } from "../../primitives";
import { hairline } from "./expandMotion";

/** 8px status dot. `hollow` renders the outline form used for "pending". */
export function StatusDot({
  tone,
  hollow = false,
}: {
  readonly tone: string;
  readonly hollow?: boolean;
}): ReactElement {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: radius.full,
        background: hollow ? "transparent" : tone,
        border: hollow ? `1.5px solid ${tone}` : "none",
        flexShrink: 0,
      }}
    />
  );
}

export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  readonly icon: ReactElement;
  readonly title: string;
  readonly hint?: string;
  readonly action?: ReactNode;
}): ReactElement {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: space[2],
        padding: space[6],
        textAlign: "center",
        minWidth: 0,
      }}
    >
      <span style={{ color: color.fg.subtle, display: "inline-flex" }}>{icon}</span>
      <Text role="body" weight="medium" tone="muted">
        {title}
      </Text>
      {hint !== undefined && (
        <Text role="caption" tone="subtle">
          {hint}
        </Text>
      )}
      {action}
    </div>
  );
}

/**
 * Board action button. Wraps the `Button` primitive and adds the two
 * outline tones the primitive does not ship: `dangerOutline` for a
 * reversible destructive choice and `successOutline` for approval that
 * is not the panel's single primary verb.
 */
export type ActionTone =
  | "primary"
  | "secondary"
  | "ghost"
  | "danger"
  | "dangerOutline"
  | "successOutline";

export function ActionButton({
  label,
  icon,
  tone = "secondary",
  disabled = false,
  busy = false,
  fullWidth = false,
  ariaLabel,
  onClick,
  size = "md",
}: {
  readonly label: string;
  readonly icon?: ReactElement;
  readonly tone?: ActionTone;
  readonly disabled?: boolean;
  readonly busy?: boolean;
  readonly fullWidth?: boolean;
  readonly ariaLabel?: string;
  readonly onClick: () => void;
  readonly size?: "sm" | "md";
}): ReactElement {
  const variant =
    tone === "primary"
      ? "primary"
      : tone === "danger"
        ? "danger"
        : tone === "secondary"
          ? "secondary"
          : "ghost";
  const outline: CSSProperties =
    tone === "dangerOutline"
      ? { color: color.accent.danger, borderColor: color.accent.danger }
      : tone === "successOutline"
        ? { color: color.accent.success, borderColor: color.accent.success }
        : {};
  return (
    <Button
      variant={variant}
      size={size}
      disabled={disabled}
      busy={busy}
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      fullWidth={fullWidth}
      style={{ ...outline, flexShrink: 0 }}
    >
      {icon}
      {label}
    </Button>
  );
}

/** Status pill for a settled task ("Completed", "Pending approval"). */
export function StatusPill({ label, tone }: { readonly label: string; readonly tone: string }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: space[2],
        height: 32,
        paddingInline: space[3],
        borderRadius: radius.pill,
        border: hairline,
        color: tone,
        background: color.bg.surface2,
        flexShrink: 0,
      }}
    >
      <StatusDot tone={tone} />
      <Text role="caption" weight="semibold" style={{ color: "inherit" }}>
        {label}
      </Text>
    </span>
  );
}

/** Small label + value pair for header metadata. */
export function MetaLine({ children }: { readonly children: ReactNode }): ReactElement {
  return (
    <Text role="caption" size="xs" tone="subtle" truncate style={{ display: "block" }}>
      {children}
    </Text>
  );
}

/**
 * One item of a panel meta row: glyph + caption. `tone` is the only color
 * channel; `danger` marks an overdue due date and nothing else.
 */
export function MetaChip({
  icon,
  children,
  tone = "muted",
  testId,
}: {
  readonly icon: ReactElement;
  readonly children: ReactNode;
  readonly tone?: "muted" | "danger";
  readonly testId?: string;
}): ReactElement {
  const fg = tone === "danger" ? color.accent.danger : color.fg.muted;
  return (
    <span
      data-testid={testId}
      data-tone={tone}
      style={{ display: "inline-flex", alignItems: "center", gap: space[1], color: fg, minWidth: 0, maxWidth: "100%" }}
    >
      <span style={{ display: "inline-flex", flexShrink: 0 }}>{icon}</span>
      <Text role="caption" size="xs" truncate style={{ color: "inherit" }}>
        {children}
      </Text>
    </span>
  );
}
